/**
 * Theme color utility for centralizing color classes
 * This file provides consistent color classes throughout the application
 */

// Text colors
export const textColors = {
  // Primary text colors
  primary: 'text-gray-800 dark:text-gray-100',
  secondary: 'text-gray-600 dark:text-gray-400',
  muted: 'text-gray-500 dark:text-gray-500',
  mutedHover: 'hover:text-gray-600 dark:hover:text-gray-400',

  // Interactive colors
  interactive: 'text-yellow-600 hover:text-yellow-500',

  // Brand colors
  brand: 'text-yellow-700',
  brandHover: 'hover:text-yellow-700 dark:hover:text-yellow-400',

  // State colors
  active: 'text-yellow-700',
  inactive: 'text-gray-400 dark:text-gray-500',

  // Feedback colors
  success: 'text-green-500 dark:text-green-400',
  successDark: 'text-green-700 dark:text-green-400',
  error: 'text-red-500 dark:text-red-400',
  warning: 'text-amber-500 dark:text-amber-400',
  info: 'text-blue-500 dark:text-blue-400',

  // Special text colors
  white: 'text-white',
  black: 'text-gray-800',

  // Link colors
  link: 'text-yellow-700 dark:text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-400',
};

// Background colors
export const bgColors = {
  // Primary backgrounds
  primary: 'bg-white dark:bg-gray-800',
  secondary: 'bg-gray-100 dark:bg-gray-900',
  tertiary: 'bg-gray-200 dark:bg-gray-700',

  // Brand backgrounds
  brand: 'bg-yellow-500',
  brandHover: 'hover:bg-yellow-600 dark:hover:bg-yellow-400',

  // State backgrounds
  active: 'bg-yellow-500/[0.12] dark:bg-yellow-500/[0.24]',
  inactive: 'bg-gray-100 dark:bg-gray-800',

  // Capsule background
  capsule: 'bg-yellow-500 dark:bg-yellow-400',

  // Feedback backgrounds
  success: 'bg-green-500',
  error: 'bg-red-500',
  errorHover: 'hover:bg-red-600 dark:hover:bg-red-400',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',

  // Special backgrounds
  white: 'bg-white',
  black: 'bg-black',
  transparent: 'bg-transparent',

  // Gradient backgrounds
  gradient: 'bg-[linear-gradient(135deg,var(--tw-gradient-stops))]',
  gradientPrimary:
    'from-yellow-500/[0.12] dark:from-yellow-500/[0.24] to-yellow-500/[0.04]',

  fill: 'fill-yellow-700 dark:fill-yellow-400 hover:fill-yellow-600 dark:hover:fill-yellow-400',
  // fill-violet-500 dark:fill-violet-400
};

// Border colors
export const borderColors = {
  primary: 'border-gray-200 dark:border-gray-700/60',
  secondary: 'border-gray-300 dark:border-gray-600',

  brand: 'border-yellow-500',

  focus: 'focus:border-yellow-500',
  ring: 'focus:ring-yellow-500',

  // Feedback borders
  success: 'border-green-500',
  error: 'border-red-500',
  warning: 'border-amber-500',
  info: 'border-blue-500',
};

// Button styles
export const buttonStyles = {
  primary:
    'btn bg-yellow-500 hover:bg-yellow-400 text-gray-700 disabled:bg-yellow-400',
  primaryDisabled: 'bg-yellow-100 text-gray-400 cursor-not-allowed',
  secondary:
    'btn bg-white border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-600',
  tertiary: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
  tertiaryDisabled: 'bg-gray-100 text-gray-400 cursor-not-allowed',
  danger: 'btn bg-red-500 hover:bg-red-600 text-gray-700',
  success: 'btn bg-green-500 hover:bg-green-600 text-gray-700',
  warning: 'btn bg-amber-500 hover:bg-amber-600 text-gray-700',
  info: 'btn bg-blue-500 hover:bg-blue-600 text-gray-700',

  disabled: 'disabled:bg-gray-400 disabled:cursor-not-allowed',
};

// Form styles
export const formStyles = {
  checkbox: 'accent-red-500/25',
};

// Shadow styles
export const shadowStyles = {
  sm: 'shadow-sm',
  md: 'shadow',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  none: 'shadow-none',
};

// Combine multiple classes
export function combineClasses(...classes: string[]): string {
  return classes.filter(Boolean).join(' ');
}

// Create theme object to export all at once
export const theme = {
  text: textColors,
  bg: bgColors,
  border: borderColors,
  button: buttonStyles,
  form: formStyles,
  shadow: shadowStyles,
  combine: combineClasses,
};

export default theme;
